/**
 * Visão geral › Agora (D559 A02-HOURLY, doc 22) — framework-neutral view-models of the "Agora" panel and of the period's hour-of-day chart.
 *
 * The server (`GET /v1/ops/dashboard/now`, read from the analytics database) names the market's local day hour by hour; this module only
 * presents it: the headline KPIs of today so far with their delta against yesterday's same elapsed time, the hourly bars (a zero is a real
 * zero, an hour that has not started is left empty, a repeated hour of a fall-back day is its own bar), and the published supply per hour
 * (an hour the store has no presence data for is `unavailable`, never 0; a real 0 is a 0). A delta over a zero base carries the absolute change only — never an
 * infinite or invented percentage. No colour literal, no country, no zone: the zone is the market's, named by the server.
 */
import type { HourOfDayWire, NowDashboardView, NowRideFigures, NowSlotView, PeriodDashboardView } from '@vello/api-client';

export type NowKpiId = 'requested' | 'completed' | 'noDriver' | 'cancelled';
export const NOW_KPI_IDS: readonly NowKpiId[] = ['requested', 'completed', 'noDriver', 'cancelled'];

/** Whether more of it is good news: completed up is good, no-driver and cancelled up are bad, requested up is just demand. */
const GOOD_WHEN_UP: Readonly<Record<NowKpiId, 'good' | 'bad' | 'neutral'>> = { requested: 'neutral', completed: 'good', noDriver: 'bad', cancelled: 'bad' };

export interface NowDelta {
  readonly direction: 'up' | 'down' | 'flat';
  /** The signed absolute change (`+3`, `-2`, `0`). */
  readonly change: number;
  /** The relative change as a fraction (`0.25` = +25 %); absent when the comparison base is zero. */
  readonly ratio?: number;
  readonly tone: 'success' | 'danger' | 'neutral';
}

/** The change of `current` against `previous` for one KPI. */
export function nowDelta(id: NowKpiId, current: number, previous: number): NowDelta {
  const change = current - previous;
  const direction = change > 0 ? 'up' : change < 0 ? 'down' : 'flat';
  const quality = GOOD_WHEN_UP[id];
  const tone = direction === 'flat' || quality === 'neutral' ? 'neutral' : (direction === 'up') === (quality === 'good') ? 'success' : 'danger';
  return { direction, change, ...(previous > 0 ? { ratio: change / previous } : {}), tone };
}

export interface NowKpi {
  readonly id: NowKpiId;
  readonly value: number;
  readonly delta: NowDelta;
}

export function nowKpis(view: NowDashboardView | undefined): readonly NowKpi[] {
  if (view === undefined) return [];
  const figure = (f: NowRideFigures, id: NowKpiId): number => f[id];
  return NOW_KPI_IDS.map((id) => ({ id, value: figure(view.totals, id), delta: nowDelta(id, figure(view.totals, id), figure(view.comparison.rides, id)) }));
}

/** The text of a delta: `+3 (+25,0 %)`, `-2`, `0`. Locale-aware; the sign is always spelled so colour and arrow never carry the meaning alone. */
export function formatNowDelta(delta: NowDelta, locale: string): string {
  const n = new Intl.NumberFormat(locale, { signDisplay: 'exceptZero' });
  const p = new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 0, signDisplay: 'exceptZero' });
  return delta.ratio === undefined || delta.change === 0 ? n.format(delta.change) : `${n.format(delta.change)} (${p.format(delta.ratio)})`;
}

/** The label under a bar: the wall-clock hour, `01h` (and `01h·2` for the second pass of a repeated hour). */
export function hourLabel(hour: number, occurrence: 1 | 2 = 1): string {
  return `${String(hour).padStart(2, '0')}h${occurrence === 2 ? '·2' : ''}`;
}

export interface HourBars {
  readonly categories: readonly string[];
  readonly requested: readonly number[];
  readonly completed: readonly number[];
  readonly noDriver: readonly number[];
  /** requested − completed − no driver: cancelled or still in flight. Never negative. */
  readonly other: readonly number[];
  /** The bar painted ink in the design look: the hour that contains "now" (absent for a period chart). */
  readonly currentIndex?: number;
}

const other = (requested: number, completed: number, noDriver: number): number => Math.max(0, requested - completed - noDriver);

/** The bars of today so far: one per slot of the day (23, 24 or 25), a future hour as an empty bar. */
export function nowBars(view: NowDashboardView | undefined): HourBars | undefined {
  if (view === undefined) return undefined;
  const slots: readonly NowSlotView[] = view.slots;
  return {
    categories: slots.map((s) => hourLabel(s.hour, s.occurrence)),
    requested: slots.map((s) => s.requested),
    completed: slots.map((s) => s.completed),
    noDriver: slots.map((s) => s.noDriver),
    other: slots.map((s) => other(s.requested, s.completed, s.noDriver)),
    currentIndex: view.currentIndex,
  };
}

/** The bars of the period's 24 wall-clock hours. `undefined` when the server did not return the series (the hourly read is not wired). */
export function hourOfDayBars(view: PeriodDashboardView | undefined): HourBars | undefined {
  const hours: readonly HourOfDayWire[] | undefined = view?.hourOfDay;
  if (hours === undefined) return undefined;
  return {
    categories: hours.map((h) => hourLabel(h.hour)),
    requested: hours.map((h) => h.requested),
    completed: hours.map((h) => h.completed),
    noDriver: hours.map((h) => h.noDriver),
    other: hours.map((h) => other(h.requested, h.completed, h.noDriver)),
  };
}

export interface SupplyHour {
  readonly label: string;
  /** The real drivers online (a true 0 when the sampler recorded the hour and nobody was online); `null` ONLY when the store has no presence data for the hour. */
  readonly driversOnline: number | null;
  readonly future: boolean;
}

/** The drivers online of each hour of the day (see {@link SupplyHour}). */
export function supplyHours(view: NowDashboardView | undefined): readonly SupplyHour[] {
  return (view?.slots ?? []).map((s) => ({ label: hourLabel(s.hour, s.occurrence), driversOnline: s.driversOnline, future: s.future }));
}

/** True when at least one started hour has presence data (otherwise the supply chart says there is none yet). */
export function hasSupplyData(view: NowDashboardView | undefined): boolean {
  return (view?.slots ?? []).some((s) => !s.future && s.driversOnline !== null);
}

/** Headline deltas of the PERIOD view against the window before it (absent while the server did not return `previous`). */
export function periodDelta(id: 'requested' | 'completed', view: PeriodDashboardView | undefined): NowDelta | undefined {
  if (view?.previous === undefined) return undefined;
  return nowDelta(id, view.rides[id], view.previous.rides[id]);
}
